import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { Plus, Search, Calendar, FileText, CheckCircle, Save, ArrowLeft, Building2, Warehouse } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function InventoriesPage() {
    const { user } = useAuth();
    const [inventories, setInventories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // list, create, edit
    const [activeInventoryId, setActiveInventoryId] = useState(null);
    const [inventoryDetails, setInventoryDetails] = useState(null); // { inventory: {}, items: [] }
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Filter
    const [searchTerm, setSearchTerm] = useState('');
    
    // Sucursales and Bodegas for form
    const [sucursales, setSucursales] = useState([]);
    const [bodegas, setBodegas] = useState([]);
    const [selectedSucursal, setSelectedSucursal] = useState('');
    const [selectedBodega, setSelectedBodega] = useState('');

    useEffect(() => {
        fetchInventories();
        fetchSucursales();
    }, []);

    useEffect(() => {
        if (selectedSucursal) {
            fetchBodegas(selectedSucursal);
        } else {
            setBodegas([]);
            setSelectedBodega('');
        }
    }, [selectedSucursal]);

    const fetchSucursales = async () => {
        try {
            const res = await fetch(`${API_URL}/api/sucursales`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                console.log('Sucursales recibidas del backend:', data);
                // Mostrar todas las sucursales (activas primero), eliminando duplicados por ID
                const uniqueSucursales = data
                    .reduce((acc, current) => {
                        const id = current.id || current.sucursal_id;
                        const existing = acc.find(item => (item.id || item.sucursal_id) === id);
                        if (!existing) {
                            acc.push(current);
                        }
                        return acc;
                    }, [])
                    .sort((a, b) => {
                        // Activas primero
                        if (a.estado === true && b.estado !== true) return -1;
                        if (a.estado !== true && b.estado === true) return 1;
                        // Luego por nombre
                        return (a.nombre || '').localeCompare(b.nombre || '');
                    });
                console.log('Sucursales únicas ordenadas:', uniqueSucursales);
                setSucursales(uniqueSucursales);
            } else {
                console.error('Error en respuesta de sucursales:', res.status, res.statusText);
                const errorText = await res.text();
                console.error('Detalles del error:', errorText);
            }
        } catch (err) {
            console.error('Error fetching sucursales:', err);
            alert('Error al cargar las sucursales. Verifica la consola para más detalles.');
        }
    };

    const fetchBodegas = async (sucursalId) => {
        try {
            console.log('Buscando bodegas para sucursal:', sucursalId);
            if (!sucursalId) {
                console.warn('No se proporcionó sucursalId');
                setBodegas([]);
                return;
            }
            
            const res = await fetch(`${API_URL}/api/bodegas/sucursal/${sucursalId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                console.log('Bodegas recibidas del backend para sucursal', sucursalId, ':', data);
                
                // Eliminar duplicados por ID
                const uniqueBodegas = data.reduce((acc, current) => {
                    const id = current.id || current.bodega_id;
                    const existing = acc.find(item => (item.id || item.bodega_id) === id);
                    if (!existing) {
                        acc.push(current);
                    }
                    return acc;
                }, []);
                
                console.log('Bodegas filtradas y únicas:', uniqueBodegas);
                setBodegas(uniqueBodegas);
                
                // Auto-select first bodega if available
                if (uniqueBodegas.length > 0 && !selectedBodega) {
                    const firstBodegaId = uniqueBodegas[0].id || uniqueBodegas[0].bodega_id;
                    console.log('Auto-seleccionando primera bodega:', firstBodegaId);
                    setSelectedBodega(firstBodegaId);
                } else if (uniqueBodegas.length === 0) {
                    console.warn('No hay bodegas para la sucursal seleccionada');
                    setSelectedBodega('');
                }
            } else {
                console.error('Error en respuesta de bodegas:', res.status, res.statusText);
                const errorText = await res.text().catch(() => '');
                console.error('Detalles del error:', errorText);
                setBodegas([]);
                setSelectedBodega('');
            }
        } catch (err) {
            console.error('Error fetching bodegas:', err);
            setBodegas([]);
            setSelectedBodega('');
        }
    };

    const fetchInventories = async () => {
        try {
            const res = await fetch(`${API_URL}/api/inventories`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setInventories(await res.json());
            }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        const glosa = e.target.glosa.value;
        const fecha = e.target.fecha.value;
        
        if (!selectedSucursal || !selectedBodega) {
            alert('Por favor selecciona una sucursal y una bodega');
            return;
        }
        
        // Disable button to prevent double submission
        const submitButton = e.target.querySelector('button[type="submit"]');
        const originalText = submitButton.textContent;
        submitButton.disabled = true;
        submitButton.textContent = 'Creando...';
        
        try {
            console.log('Intentando crear inventario en:', `${API_URL}/api/inventories`);
            const res = await fetch(`${API_URL}/api/inventories`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ 
                    glosa, 
                    fecha, 
                    sucursal_id: parseInt(selectedSucursal),
                    bodega_id: parseInt(selectedBodega)
                })
            });
            
            console.log('Respuesta del servidor:', res.status, res.statusText);
            
            if (res.ok) {
                const newInv = await res.json();
                console.log('Inventario creado:', newInv);
                await fetchInventories();
                await openInventory(newInv.id);
            } else {
                let errorData;
                try {
                    errorData = await res.json();
                } catch {
                    errorData = { error: `Error ${res.status}: ${res.statusText}` };
                }
                console.error('Error al crear inventario:', errorData);
                alert(`Error al crear el inventario: ${errorData.error || res.statusText}`);
            }
        } catch (err) {
            console.error('Error en handleCreate:', err);
            console.error('API_URL configurada:', API_URL);
            console.error('Tipo de error:', err.name);
            console.error('Mensaje de error:', err.message);
            
            if (err.message.includes('Failed to fetch') || err.message.includes('ERR_CONNECTION_REFUSED')) {
                alert(`No se pudo conectar al servidor. Verifica que el backend esté corriendo en ${API_URL}`);
            } else {
                alert(`Error de conexión: ${err.message}`);
            }
        } finally {
            submitButton.disabled = false;
            submitButton.textContent = originalText;
        }
    };

    const handleCancelCreate = () => {
        setView('list');
        setSelectedSucursal('');
        setSelectedBodega('');
        setBodegas([]);
    };

    const openInventory = async (id) => {
        setActiveInventoryId(id);
        setView('edit');
        setDetailsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/inventories/${id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setInventoryDetails(await res.json());
            }
        } catch (err) { console.error(err); } finally { setDetailsLoading(false); }
    };

    const handleUpdateItem = async (itemId, newQty) => {
        // Optimistic update
        setInventoryDetails(prev => ({
            ...prev,
            items: prev.items.map(item => item.id === itemId ? { ...item, stock_fisico: newQty } : item)
        }));

        try {
            await fetch(`${API_URL}/api/inventories/${activeInventoryId}/items/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ stock_fisico: newQty })
            });
        } catch (err) { console.error(err); }
    };

    const handleUpdateFechaVencimiento = async (itemId, fecha) => {
        setInventoryDetails(prev => ({
            ...prev,
            items: prev.items.map(item => {
                if (item.id !== itemId) return item;
                return { ...item, fecha_vencimiento: fecha || null, fecha_vencimiento_lote: fecha || item.fecha_vencimiento_lote };
            })
        }));
        try {
            const res = await fetch(`${API_URL}/api/inventories/${activeInventoryId}/items/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ fecha_vencimiento: fecha || null })
            });
            if (!res.ok) throw new Error('Error al actualizar');
        } catch (err) {
            console.error(err);
            alert('Error al actualizar fecha de vencimiento');
        }
    };

    const handleFinalize = async () => {
        if (!confirm('¿Estás seguro de finalizar el inventario? Esto actualizará el stock del sistema.')) return;
        try {
            const res = await fetch(`${API_URL}/api/inventories/${activeInventoryId}/finalize`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                alert('Inventario finalizado correctamente');
                setView('list');
                fetchInventories();
            } else {
                alert('Error al finalizar');
            }
        } catch (err) { console.error(err); }
    };

    // Group items by category - put "Sin Categoría" at the end
    const groupedItems = inventoryDetails?.items?.reduce((acc, item) => {
        const cat = item.categoria_nombre || 'Sin Categoría';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
    }, {}) || {};
    
    // Sort categories: "Sin Categoría" always last
    const sortedCategories = Object.keys(groupedItems).sort((a, b) => {
        if (a === 'Sin Categoría') return 1;
        if (b === 'Sin Categoría') return -1;
        return a.localeCompare(b);
    });

    // Filter items by search term
    const filterItems = (items) => {
        if (!searchTerm) return items;
        return items.filter(item => 
            item.producto_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.codigo_barra?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    if (view === 'edit' && inventoryDetails) {
        const totalItems = inventoryDetails.items.length;
        const itemsWithDiff = inventoryDetails.items.filter(i => (i.stock_fisico || 0) !== i.stock_sistema).length;

        return (
            <div className="page inventory-edit-page">
                <div className="flex items-center gap-4 mb-6">
                    <button onClick={() => setView('list')} className="p-2 hover:bg-gray-100 rounded-full">
                        <ArrowLeft size={24} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            Inventario #{inventoryDetails.inventory.id}
                            <span className={`text-sm px-2 py-1 rounded ${inventoryDetails.inventory.estado === 'PENDIENTE' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                {inventoryDetails.inventory.estado}
                            </span>
                        </h1>
                        <div className="text-gray-500 flex items-center gap-4">
                            <span>{inventoryDetails.inventory.glosa} - {new Date(inventoryDetails.inventory.fecha).toLocaleDateString()}</span>
                            {inventoryDetails.inventory.sucursal_nombre && (
                                <span className="flex items-center gap-1">
                                    <Building2 size={14} />
                                    {inventoryDetails.inventory.sucursal_nombre}
                                </span>
                            )}
                            {inventoryDetails.inventory.bodega_nombre && (
                                <span className="flex items-center gap-1">
                                    <Warehouse size={14} />
                                    {inventoryDetails.inventory.bodega_nombre}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-4">
                        <div className="text-sm text-gray-500">
                            <span className="font-bold text-gray-700">{totalItems}</span> productos | 
                            <span className={`font-bold ${itemsWithDiff > 0 ? 'text-orange-600' : 'text-green-600'}`}> {itemsWithDiff}</span> con diferencia
                        </div>
                        {inventoryDetails.inventory.estado === 'PENDIENTE' && (
                            <button onClick={handleFinalize} className="btn-primary">
                                <CheckCircle size={20} className="mr-2" /> Finalizar y Ajustar Stock
                            </button>
                        )}
                    </div>
                </div>

                {/* Search bar */}
                <div className="mb-4">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
                        <input
                            type="text"
                            placeholder="Buscar producto por nombre o código..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-14 pr-4 py-2 border rounded-lg"
                        />
                    </div>
                </div>

                {/* Products grouped by category */}
                <div className="space-y-6">
                    {sortedCategories.map((categoria) => {
                        const items = groupedItems[categoria];
                        const filteredItems = filterItems(items);
                        if (filteredItems.length === 0) return null;
                        
                        const isSinCategoria = categoria === 'Sin Categoría';
                        
                        return (
                            <div key={categoria} className="bg-white rounded-lg shadow overflow-hidden">
                                <div className={`px-4 py-3 font-bold flex justify-between items-center ${
                                    isSinCategoria 
                                        ? 'bg-gray-500 text-white' 
                                        : 'bg-indigo-500 text-gray-900'
                                }`}>
                                    <span>{categoria}</span>
                                    <span className="text-sm font-normal opacity-80">{filteredItems.length} productos</span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-gray-50 border-b">
                                            <tr>
                                                <th className="p-3 text-sm">Producto</th>
                                                <th className="p-3 w-28 text-center text-sm">Stock Sistema</th>
                                                <th className="p-3 w-28 text-center text-sm">Conteo Físico</th>
                                                <th className="p-3 w-32 text-center text-sm">Fecha Vencimiento</th>
                                                <th className="p-3 w-28 text-center text-sm">Diferencia</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredItems.map((item, index) => {
                                                const diff = (item.stock_fisico || 0) - item.stock_sistema;
                                                const fechaVencimiento = item.fecha_vencimiento_lote || item.fecha_vencimiento;
                                                const daysUntilExpiry = fechaVencimiento ? Math.ceil((new Date(fechaVencimiento) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                                                // Use a unique key combining inventario_id, producto_id, and index to avoid duplicates
                                                const uniqueKey = `inv_${inventoryDetails.inventory.id}_prod_${item.producto_id}_idx_${index}`;
                                                return (
                                                    <tr key={uniqueKey} className={`border-b hover:bg-gray-50 ${diff !== 0 ? 'bg-orange-50' : ''}`}>
                                                        <td className="p-3">
                                                            <div className="font-medium">{item.producto_nombre}</div>
                                                            <div className="text-xs text-gray-500">{item.codigo_barra} {item.subcategoria_nombre !== 'Sin Subcategoría' && `• ${item.subcategoria_nombre}`}</div>
                                                        </td>
                                                        <td className="p-3 text-center font-mono">{item.stock_sistema}</td>
                                                        <td className="p-3 text-center">
                                                            {inventoryDetails.inventory.estado === 'PENDIENTE' ? (
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    className="w-20 p-2 border rounded text-center font-bold font-mono"
                                                                    value={item.stock_fisico}
                                                                    onChange={(e) => handleUpdateItem(item.id, parseInt(e.target.value) || 0)}
                                                                />
                                                            ) : (
                                                                <span className="font-bold font-mono">{item.stock_fisico}</span>
                                                            )}
                                                        </td>
                                                        <td className="p-3 text-center">
                                                            {inventoryDetails.inventory.estado === 'PENDIENTE' ? (
                                                                <input
                                                                    type="date"
                                                                    className="w-32 p-2 border rounded text-sm"
                                                                    value={(fechaVencimiento || '').toString().split('T')[0] || ''}
                                                                    onChange={(e) => handleUpdateFechaVencimiento(item.id, e.target.value || null)}
                                                                />
                                                            ) : fechaVencimiento ? (
                                                                <div className="text-xs">
                                                                    <div className={daysUntilExpiry !== null && daysUntilExpiry <= 30 ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                                                                        {new Date(fechaVencimiento).toLocaleDateString()}
                                                                    </div>
                                                                    {daysUntilExpiry !== null && (
                                                                        <div className={`text-xs ${daysUntilExpiry <= 7 ? 'text-red-600' : daysUntilExpiry <= 30 ? 'text-orange-600' : 'text-gray-500'}`}>
                                                                            {daysUntilExpiry > 0 ? `${daysUntilExpiry} días` : daysUntilExpiry === 0 ? 'Hoy' : `Vencido hace ${Math.abs(daysUntilExpiry)} días`}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 text-xs">Sin fecha</span>
                                                            )}
                                                        </td>
                                                        <td className={`p-3 text-center font-bold font-mono ${diff < 0 ? 'text-red-600' : diff > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                                            {diff > 0 ? '+' : ''}{diff}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="page inventory-list-page">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold">Tomas de Inventario</h1>
                    <p className="text-gray-500">Gestione el stock físico de sus sucursales</p>
                </div>
                <button onClick={() => setView('create')} className="btn-primary">
                    <Plus size={20} className="mr-2" /> Nueva Toma
                </button>
            </div>

            {view === 'create' && (
                <div className="mb-6 bg-white p-6 rounded-lg shadow border border-indigo-100">
                    <h3 className="font-bold mb-4">Iniciar Nueva Toma de Inventario</h3>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Fecha</label>
                                <input 
                                    type="date" 
                                    name="fecha" 
                                    defaultValue={new Date().toISOString().split('T')[0]}
                                    required 
                                    className="w-full p-2 border rounded" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Sucursal *</label>
                                <select
                                    value={selectedSucursal}
                                    onChange={(e) => {
                                        setSelectedSucursal(e.target.value);
                                        setSelectedBodega('');
                                    }}
                                    required
                                    className="w-full p-2 border rounded"
                                >
                                    <option value="">Seleccionar sucursal...</option>
                                    {sucursales.map(s => {
                                        const id = s.id || s.sucursal_id;
                                        const isActive = s.estado === true;
                                        return (
                                            <option key={`sucursal_${id}_${s.nombre}`} value={id}>
                                                {s.nombre} {!isActive && '(Inactiva)'}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Bodega *</label>
                                <select
                                    value={selectedBodega}
                                    onChange={(e) => setSelectedBodega(e.target.value)}
                                    required
                                    disabled={!selectedSucursal || bodegas.length === 0}
                                    className="w-full p-2 border rounded"
                                >
                                    <option value="">
                                        {!selectedSucursal 
                                            ? 'Selecciona una sucursal primero...' 
                                            : bodegas.length === 0 
                                                ? 'No hay bodegas para esta sucursal' 
                                                : 'Seleccionar bodega...'}
                                    </option>
                                    {bodegas.map(b => {
                                        const id = b.id || b.bodega_id;
                                        return (
                                            <option key={`bodega_${id}_${b.nombre}`} value={id}>
                                                {b.nombre}
                                            </option>
                                        );
                                    })}
                                </select>
                                {selectedSucursal && bodegas.length === 0 && (
                                    <p className="text-xs text-orange-600 mt-1">
                                        ⚠️ Esta sucursal no tiene bodegas asociadas. Verifica la configuración.
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Descripción / Glosa</label>
                                <input name="glosa" required className="w-full p-2 border rounded" placeholder="Ej: Inventario Mensual Marzo" />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button type="button" onClick={handleCancelCreate} className="btn-secondary">Cancelar</button>
                            <button type="submit" className="btn-primary" disabled={!selectedSucursal || !selectedBodega}>
                                Iniciar Inventario
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid gap-4">
                {inventories.map((inv, index) => {
                    // Use a unique key combining id, created_at, and index to avoid duplicates
                    const uniqueKey = `inv_${inv.id}_${inv.created_at}_${index}`;
                    return (
                    <div key={uniqueKey} onClick={() => openInventory(inv.id)} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-full ${inv.estado === 'PENDIENTE' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600'}`}>
                                <FileText size={24} />
                            </div>
                            <div>
                                <h3 className="font-bold text-lg">#{inv.id} - {inv.glosa}</h3>
                                <div className="text-sm text-gray-500 flex gap-4 flex-wrap">
                                    <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(inv.created_at).toLocaleDateString()}</span>
                                    <span>Usuario: {inv.usuario_nombre}</span>
                                    {inv.sucursal_nombre && (
                                        <span className="flex items-center gap-1">
                                            <Building2 size={14} />
                                            {inv.sucursal_nombre}
                                        </span>
                                    )}
                                    {inv.bodega_nombre && (
                                        <span className="flex items-center gap-1">
                                            <Warehouse size={14} />
                                            {inv.bodega_nombre}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${inv.estado === 'PENDIENTE' ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-700'}`}>
                                {inv.estado}
                            </span>
                            <ChevronRightButton />
                        </div>
                    </div>
                    );
                })}
            </div>
        </div>
    );
}

function ChevronRightButton() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
            <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
    )
}
